/**
 * 画数ブラックジャック。
 *
 * 漢字の札を引いて、画数の合計を 21 に近づける。超えたら負け。
 * **札は3枚の中から選ぶ。画数は引くまで分からない。**
 * 「あと3画まで大丈夫……この字、何画だ？」を遊びの真ん中に置くため。
 *
 * 札は `scripts/build_deck.mjs` が作った `bj-deck.ts`（小学校で習う漢字）。
 */
import { BJ_DECK } from './bj-deck.js';
import { openXPostOnWeb } from './x-post.js';
import './pages.css';

/** 目指す画数 */
const GOAL = 21;
/** 親はここまで引く */
const DEALER_STAND = 17;
/** 何回勝負か */
const ROUNDS = 5;
/** 場に出す候補の数 */
const CHOICES = 3;

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => document.getElementById(id) as T;
const esc = (s: string): string =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);

interface Card {
  ch: string;
  n: number;
}

const STROKES = Object.keys(BJ_DECK)
  .map(Number)
  .sort((a, b) => a - b);

/** 1枚つくる。画数はそろえて選ぶので、大きい画数ばかりにならない */
function draw(used: Set<string>): Card {
  for (let i = 0; i < 40; i++) {
    const n = STROKES[Math.floor(Math.random() * STROKES.length)] as number;
    const pool = [...(BJ_DECK[n] ?? '')].filter((c) => !used.has(c));
    if (!pool.length) continue;
    const ch = pool[Math.floor(Math.random() * pool.length)] as string;
    used.add(ch);
    return { ch, n };
  }
  // ほぼ起きないが、念のため
  return { ch: '一', n: 1 };
}

const sum = (cards: Card[]): number => cards.reduce((s, c) => s + c.n, 0);

type Phase = 'play' | 'dealer' | 'over';
type Result = 'win' | 'lose' | 'draw';

let round = 0;
let wins = 0;
let loses = 0;
let draws = 0;
/** バーストした回数。最後のひとことを選ぶのに使う */
let busts = 0;
let used = new Set<string>();
let mine: Card[] = [];
let theirs: Card[] = [];
let choices: Card[] = [];
let phase: Phase = 'play';
/** 親の2枚目を伏せているか */
let hidden = true;
let busy = false;

function cardHtml(c: Card | null, opts: { back?: boolean; fresh?: boolean } = {}): string {
  if (opts.back || !c) return '<li class="card facedown"><span>字</span></li>';
  // トランプの角の数字のように、画数を左上と右下に出す
  return (
    `<li class="card${opts.fresh ? ' fresh' : ''}">` +
    `<i class="pip tl">${c.n}</i><b>${esc(c.ch)}</b><i class="pip br">${c.n}</i></li>`
  );
}

function render(fresh?: Card): void {
  const me = sum(mine);
  $('myCards').innerHTML = mine.map((c) => cardHtml(c, { fresh: c === fresh })).join('');
  $('myTotal').textContent = String(me);
  const state = me > GOAL ? 'bust' : me === GOAL ? 'just' : me >= GOAL - 4 ? 'near' : '';
  $('myTotal').className = `chip ${state}`;
  // 21 までの近さを、棒で見せる
  $('gaugeBar').style.width = `${Math.min(100, (me / GOAL) * 100)}%`;
  $('gaugeBar').className = state;
  $('left').textContent =
    me > GOAL ? `${me - GOAL}画 オーバー` : me === GOAL ? 'ぴったり！' : `あと ${GOAL - me}画`;
  $('left').className = `left ${state}`;
  const shown = hidden ? theirs.slice(0, 1) : theirs;
  $('theirCards').innerHTML =
    shown.map((c) => cardHtml(c)).join('') + (hidden ? cardHtml(null, { back: true }) : '');
  $('theirTotal').textContent = hidden ? '？' : String(sum(theirs));
  $('theirTotal').className = `chip${!hidden && sum(theirs) > GOAL ? ' bust' : ''}`;
  $('round').textContent = `${Math.min(round + 1, ROUNDS)} / ${ROUNDS} 回戦`;
  $('record').textContent = `${wins}勝 ${loses}敗${draws ? ` ${draws}分` : ''}`;
  // **伏せたまま選ぶ。** 字が見えていると、知っている人には画数が分かってしまう
  $('choices').innerHTML = choices
    .map((_, i) => `<button type="button" class="facedown" data-i="${i}"><span>字</span></button>`)
    .join('');
  const playing = phase === 'play';
  $('choiceArea').hidden = !playing;
  $('stand').hidden = !playing;
  // 最後の回のあとは「次の回へ」を出さない（すぐ結果に変わるので、ちらつく）
  $('nextRound').hidden = phase !== 'over' || round >= ROUNDS;
}

function say(html: string, kind = ''): void {
  const el = $('say');
  el.innerHTML = html;
  el.className = `say ${kind}`;
}

function newRound(): void {
  used = new Set();
  mine = [draw(used), draw(used)];
  theirs = [draw(used), draw(used)];
  choices = [...Array(CHOICES)].map(() => draw(used));
  phase = 'play';
  hidden = true;
  busy = false;
  $('done').hidden = true;
  if (sum(mine) === GOAL) {
    say(`いきなり<b>${GOAL}画ぴったり</b>！`, 'good');
  } else {
    say(`いまの合計は <b>${sum(mine)}画</b>。あと <b>${GOAL - sum(mine)}画</b>まで。`);
  }
  render();
}

const wait = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/** 場の1枚を引く。画数は少し置いてから見せる（「何画だ？」の間） */
async function take(i: number): Promise<void> {
  if (busy || phase !== 'play') return;
  const card = choices[i];
  if (!card) return;
  busy = true;
  // めくって字だけ見せる。画数が出るのは、そのあと
  const btn = $('choices').querySelector<HTMLButtonElement>(`button[data-i="${i}"]`);
  if (btn) {
    btn.classList.remove('facedown');
    btn.classList.add('picked', 'flip');
    btn.innerHTML = `<i class="pip tl">?</i><b>${esc(card.ch)}</b><i class="pip br">?</i>`;
  }
  say(`<b>${esc(card.ch)}</b>… 何画だ？`);
  await wait(750);
  mine.push(card);
  choices = [...Array(CHOICES)].map(() => draw(used));
  const total = sum(mine);
  render(card);
  if (total > GOAL) {
    busts++;
    say(`<b>${card.n}画</b>！ 合計 ${total}画でバースト。`, 'bad');
    await finish('lose');
    return;
  }
  if (total === GOAL) {
    say(`<b>${card.n}画</b>！ <b>${GOAL}画ぴったり</b>！`, 'good');
    await wait(500);
    await dealerTurn();
    return;
  }
  say(`<b>${card.n}画</b>。合計 ${total}画。あと <b>${GOAL - total}画</b>まで。`);
  busy = false;
}

/** とめる → 親の番 */
async function dealerTurn(): Promise<void> {
  busy = true;
  phase = 'dealer';
  hidden = false;
  render();
  await wait(600);
  while (sum(theirs) < DEALER_STAND) {
    theirs.push(draw(used));
    render();
    say(`親が引いた。親は <b>${sum(theirs)}画</b>。`);
    await wait(700);
  }
  const me = sum(mine);
  const them = sum(theirs);
  if (them > GOAL) {
    say(`親は ${them}画でバースト。<b>あなたの勝ち！</b>`, 'good');
    await finish('win');
  } else if (me > them) {
    say(`${me}画 対 ${them}画。<b>あなたの勝ち！</b>`, 'good');
    await finish('win');
  } else if (me < them) {
    say(`${me}画 対 ${them}画。<b>親の勝ち。</b>`, 'bad');
    await finish('lose');
  } else {
    say(`${me}画 対 ${them}画で<b>引き分け</b>。`);
    await finish('draw');
  }
}

async function finish(r: Result): Promise<void> {
  if (r === 'win') wins++;
  else if (r === 'lose') loses++;
  else draws++;
  phase = 'over';
  hidden = false;
  round++;
  render();
  await wait(100);
  if (round >= ROUNDS) showDone();
}

function showDone(): void {
  $('game').hidden = true;
  const done = $('done');
  done.hidden = false;
  $('doneRecord').textContent = `${wins}勝 ${loses}敗${draws ? ` ${draws}分` : ''}`;
  const [title, msg] =
    wins === ROUNDS
      ? ['画数の達人', '全勝。漢字の画数が体に入っていますね。']
      : wins >= 3
        ? ['なかなかの目', '勝ち越し。引きどころが分かっています。']
        : wins === loses
          ? ['五分五分', 'もう一回やれば、きっと勝ち越せます。']
          : busts >= 2
            ? ['引きすぎた人', '「あと3画」のつもりが、だいたい5画でした。']
            : ['とめるのが早い人', '親に押し切られました。もう1枚、いけたかもしれません。'];
  $('doneTitle').textContent = title;
  $('doneMsg').textContent = msg;
  shareText =
    `画数ブラックジャックで ${wins}勝${loses}敗${draws ? `${draws}分` : ''}（${title}）\n` +
    `漢字を引いて、画数の合計を21に近づけるゲーム。\nhttps://mojitower.abarabob.com/blackjack`;
  $<HTMLAnchorElement>('share').href = `https://x.com/intent/post?text=${encodeURIComponent(shareText)}`;
}

let shareText = '';

$('choices').addEventListener('click', (e) => {
  const b = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-i]');
  if (b) void take(Number(b.dataset['i']));
});
$('stand').addEventListener('click', () => {
  if (!busy && phase === 'play') void dealerTurn();
});
$('nextRound').addEventListener('click', () => newRound());
$('again').addEventListener('click', () => {
  round = 0;
  wins = 0;
  loses = 0;
  draws = 0;
  busts = 0;
  $('done').hidden = true;
  $('game').hidden = false;
  newRound();
});
$('share').addEventListener('click', (e) => {
  const url = $<HTMLAnchorElement>('share').href;
  if (openXPostOnWeb(shareText, url)) e.preventDefault();
});

newRound();
